import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';
import * as Constants from 'src/constants';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-04-22'
        tag='Facebook Marketplace'
        title='Sellers Guide 101:Facebook Marketplace Tax Identification'
      >
        <p>
          Facebook Marketplace is a great place to buy and sell goods. With the convenience of buying and selling from
          your phone, it's also a lot easier than traditional methods like eBay or Craigslist. One thing that you should
          always keep in mind when selling on Facebook Marketplace is your tax responsibilities.
        </p>
        <p>
          The recent popularity of Facebook Marketplace has led to many people selling items and earning money from the
          platform. As with any business, it is important to stay tax-compliant. This article will cover some of the key
          points to remember to avoid any tax implications while earning money by selling on Facebook Marketplace.
        </p>

        <h3>Why Your Tax ID Is Needed </h3>
        <p>
          Facebook needs to collect your tax information to comply with applicable laws and regulations. This way, they
          can ensure that their sellers pay their taxes on time. Facebook also guarantees that the tax information won't
          be visible or shared on Facebook.{' '}
        </p>

        <h3>Collecting Tax Information </h3>
        <p>
          Facebook will collect tax information for new and existing sellers beginning January 1, 2022. This includes
          your Social Security Number (SSN) or Individual Tax Identification Number (ITIN). This tax ID information is
          needed to ensure Facebook is following its obligations to the IRS and ensuring that sellers can still use and
          access features to accept payments for their direct sales on Marketplace.{' '}
        </p>
        <p>Facebook may ask for the following tax information: </p>
        <ul>
          <li>The Last four digits of your SSN or ITIN</li>
          <li>
            Full nine digits of your SSN or ITIN (For sellers reaching $499 in sales with checkout on Marketplace){' '}
          </li>
        </ul>
        <p>
          The recent change in tax laws and threshold requires Facebook to collect sales tax and remit it on behalf of
          the sellers. Meaning that Facebook and Instagram sellers are no longer needed to send sales tax on each of
          their transactions themselves, as Facebook will collect and remit it on the seller's behalf.{' '}
        </p>

        <h3>Updating Tax Information for New and Existing Sellers</h3>
        <p>
          Users who want to sell on Facebook with checkout need to provide their tax information before setting up their
          marketplace accounts. This is to avoid any tax implications as your sales may be subject to the state or local
          sales tax. And as a seller, you need to identify the correct way to handle sales tax for your sales.{' '}
        </p>
        <p>
          If you are a new or existing marketplace seller, here are some things you must be aware of when Facebook asks
          for your last four digits, SSN or ITIN, or your full 9-digits SSN or ITIN.{' '}
        </p>

        <h4>Last 4 Digits SSN or ITIN</h4>
        <ul>
          <li>
            <strong>New Sellers:</strong> New accounts signing up for the first time to sell with checkout on
            Marketplace will be asked to provide their last four digits, SSN or ITIN. New sellers would not be able to
            finish setting up their accounts unless they provided the needed tax information. Furthermore, the tax
            information must also be verified by Facebook before they can start selling.
          </li>
          <li>
            <strong>Existing Sellers:</strong> If you used to sell with checkout on Marketplace without providing your
            tax information in the past, now is an excellent time to update or provide it. Especially if Facebook
            prompts you to do so. If you don't, your selling activity will be restricted. This includes disabling your
            account temporarily from accepting orders or canceling all unshipped orders until you provide the needed tax
            information within five days of the request. Facebook must also verify the tax information before you can
            continue selling.{' '}
          </li>
        </ul>

        <h4>Full 9 Digits SSN or ITIN </h4>
        <p>
          New and existing sellers who can provide their full 9-digits SSN or ITIN, and were successfully verified, need
          not worry about any additional supporting documents that may be asked during the verification process.
          Facebook ensures that all tax information is kept confidential and will not be shared on Facebook.{' '}
        </p>

        <h4>How to Add Your Tax Information</h4>
        <p>
          You may add your tax information, either the last four digits or full nine digits SSN or ITIN, in a banner of
          your <strong>Sales Section</strong> of the app.{' '}
        </p>

        <h4>Verification Process</h4>
        <p>
          All sellers who reach $499 in sales starting January 1, 2022, have 14 days to provide their tax information.
          Sellers who fail to provide the needed tax information within the 14 days can still access marketplace
          features and receive orders from their buyers, but their payouts for their sales will be put on hold.{' '}
        </p>

        <h3>Acceptable Forms of Identification </h3>
        <p>
          To ensure a smooth verification process, sellers must be familiarized with acceptable forms of identification
          when setting up shipping on their Facebook Marketplace. You must submit your tax documentation and proof of
          your identity to prevent your payouts from being put on hold.{' '}
        </p>
        <p>The following are the summarized acceptable forms you can submit to confirm your account:</p>

        <h4>Proof of Identity </h4>
        <p>
          You must submit documents that include your identification information. This includes but is not limited to
          the following:{' '}
        </p>
        <ul>
          <li>First and Last name (The name should match the registered name on your Facebook Marketplace profile) </li>
          <li>Date of birth</li>
          <li>Document ID Number (with the issuance and expiry date) </li>
        </ul>

        <p>
          <strong>Acceptable Proof of Identity documents:</strong>
        </p>
        <ul>
          <li>Passport or Passport Card (Front and Back)</li>
          <li>Driver's license (Front and Back)</li>
          <li>Any State or Government Issued ID (Front and Back)</li>
        </ul>

        <p>
          <strong>Not Acceptable:</strong>
        </p>
        <ul>
          <li>Military IDs</li>
          <li>Medical cards </li>
          <li>Student cards</li>
          <li>Any expired, partial, or obscured documents. </li>
        </ul>

        <p>
          Those who recently changed their names and other information on their identity that doesn't match their
          identity document must submit additional documentation verifying the change in their identification
          information.
        </p>

        <h4>Proof of Address </h4>
        <p>
          You must submit proof of your address or the place where you are currently residing. It must include your full
          name and residential address. Only physical addresses are valid, not PO Box addresses.{' '}
        </p>

        <p>
          <strong>Acceptable Proof of Address Documents:</strong>
        </p>
        <ul>
          <li>Utility Bill (Must be dated within the last 12 months)</li>
          <li>Cellular or landline bill (Must be dated within the previous 12 months)</li>
          <li>Motor vehicle registration (Must be dated within the previous 12 months)</li>
          <li>401k/Brokerage Statement (Must be dated within the previous 12 months)</li>
          <li>Copy of Lease Agreement for Home or Grant Deed on Home</li>
          <li>Bank Statement (Must be dated within the previous 12 months)</li>
          <li>Identity card or driver's license with a physical address (Must be where you are currently living) </li>
        </ul>

        <p>
          <strong>Unacceptable documents:</strong>
        </p>
        <ul>
          <li>Military IDs</li>
          <li>Waybills, receipts, invoices </li>
          <li>Any expired, partial, or blurred documents.</li>
        </ul>

        <h4>Proof of Your SSN/ITIN</h4>
        <p>
          You must submit your full nine digits Social Security Number (SSN), or your Individual Taxpayer Identification
          Number (ITIN) if you cannot get an SSN. An ITIN is a tax processing number only available to certain
          nonresidents and resident aliens, which you can apply from Internal Revenue Services (IRS).
        </p>

        <p>
          <strong>Acceptable Proof of your SSN/ITIN Documents:</strong>
        </p>
        <ul>
          <li>Social Security Number Card </li>
          <li>SSN or ITIN Letter from IRS</li>
          <li>1099 Form (Must be dated within the last 12 months) </li>
          <li>Employer-issued W2 (Must be dated within the previous 12 months) </li>
          <li>Payslip with your complete SSN (Must be dated within the previous 12 months) </li>
          <li>
            Third-party prepared tax documents (Must be dated within the previous 12 months and signed by the preparer)
          </li>
        </ul>

        <p>
          <strong>Unacceptable Documents:</strong>
        </p>
        <ul>
          <li>Self-written and completed applications</li>
          <li>Self-prepared tax documents</li>
          <li>Any expired, blurred, or partial records. </li>
        </ul>

        <p>
          You have 7-days to submit these documents when it prompted to your account. Suppose you don't have any access
          to the needed tax documentation. In that case, you may go to your local Social Security office and request a
          letter, which you can use to confirm your Social Security Number.{' '}
        </p>

        <h3>What to do for sellers experiencing issues in verifying their accounts</h3>
        <p>
          Some sellers who already submitted the required tax information and confirmed their identity may still
          experience issues in verifying their accounts. This is because Facebook Marketplace follows a strict procedure
          in verifying selling accounts with shipping on Facebook Marketplace.{' '}
        </p>
        <p>Here are a few extra steps you can do to ensure a quick way to verify your identification: </p>
        <ul>
          <li>
            Facebook may request to have your tax information and identification re-submit again to ensure all the
            documents you provided are valid and in the correct format. Make sure that the documents you submitted are
            readable.
          </li>
          <li>
            If you have a pending review status for more than a week after submitting your tax documentation, you must
            seek further assistance from Facebook support. You may contact them through the{' '}
            <strong>Shipping Orders</strong> section of the Marketplace. Select <strong>Contact Support</strong> &gt;
            Choose one of your recent shipped-out orders.{' '}
          </li>
        </ul>

        <p>
          All payouts and transfers of your sales to your bank account will be put on hold until your account has been
          verified successfully. The verification process can take some time, depending on your submitted documentation.{' '}
        </p>

        <h3>Do More While You Wait</h3>
        <p>
          While you wait for your verification to be completed, you can do other things to improve your reselling
          venture, such as cross-listing your items to several other platforms. Cross-listing or cross-posting aids in
          increasing your product's exposure and helps you win more customers not just on the Facebook Marketplace but
          on other platforms too.{' '}
        </p>
        <p>
          One helpful tool you can use to easily create drafts and cross-list them on various platforms is through
          OneShop. Their intelligent system also automatically delists items once you sell, preventing sellers from
          selling a single item across multiple marketplaces.{' '}
        </p>
        <p>
          Aside from these helpful seller features, you can also take your time during this period to track your seller
          performance. OneShop lets you set your monthly sales goals, improve your listing, and track your sales to get
          a competitive edge against other sellers.{' '}
        </p>
        <p>
          See the difference OneShop can make in your online reselling business.{' '}
          <a href='https://tools.oneshop.com/signup'>Sign up today</a> and try our{' '}
          <>{Constants.Marketing.TrialDuration}</>
          -day risk-free trial and be one among the thousands of marketplace resellers who became successful by using
          the leading operating system for online sellers.{' '}
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
